import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WizardStep from '../components/ExitReadiness/WizardStep';
import LeadCaptureForm from '../components/ExitReadiness/LeadCaptureForm';
import SequentialQuestionStep from '../components/ExitReadiness/SequentialQuestionStep';
import BusinessExplanationStep from '../components/ExitReadiness/BusinessExplanationStep';
import PersonalExplanationStep from '../components/ExitReadiness/PersonalExplanationStep';
import IndustrySelector from '../components/ExitReadiness/IndustrySelector';
import {
  startLeadAssessment,
  getLeadAssessmentQuestions,
  saveLeadAssessmentResponse,
  calculateLeadAssessmentResults,
  submitLeadInformation,
  saveSessionToken,
  getSessionToken,
  saveResponsesLocally,
  getLocalResponses,
  clearLocalResponses,
  saveValuationLocally,
  getLocalValuation,
  clearLocalValuation,
  saveWealthDataLocally,
  getLocalWealthData,
  clearLocalWealthData
} from '../services/leadAssessmentApi';
import './ExitReadinessAssessment.css';

const ExitReadinessAssessment = () => {
  const navigate = useNavigate();

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionToken, setSessionToken] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculatedResults, setCalculatedResults] = useState(null);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);

  // Total steps
  const TOTAL_STEPS = 9;

  // Preload background image asynchronously
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setBackgroundLoaded(true);
    };
    img.src = `${process.env.PUBLIC_URL}/Background.png`;
  }, []);

  // Initialize assessment
  useEffect(() => {
    initializeAssessment();
  }, []);

  const initializeAssessment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check for existing session
      const existingToken = getSessionToken();
      const localResponses = getLocalResponses();

      let token = existingToken;

      // Start new assessment if no existing session
      if (!token) {
        const startResult = await startLeadAssessment();
        if (!startResult.success) {
          throw new Error(startResult.error);
        }
        token = startResult.data.session_token;
        saveSessionToken(token);
      }

      setSessionToken(token);

      // Load questions
      const questionsResult = await getLeadAssessmentQuestions();
      if (!questionsResult.success) {
        throw new Error(questionsResult.error);
      }

      setQuestions(questionsResult.data.questions);

      // Restore saved responses
      if (Object.keys(localResponses).length > 0) {
        setResponses(localResponses);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error initializing assessment:', err);
      setError(err.message || 'Failed to initialize assessment');
      setLoading(false);
    }
  };

  // Save response
  const handleSaveResponse = async (questionId, questionText, category, questionType, answerValue, answerNumeric = null) => {
    try {
      // Save to backend
      const result = await saveLeadAssessmentResponse({
        session_token: sessionToken,
        question_id: questionId,
        question_text: questionText,
        category: category,
        question_type: questionType,
        answer_value: answerValue,
        answer_numeric: answerNumeric
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      // Update local state
      const newResponses = {
        ...responses,
        [questionId]: {
          answer_value: answerValue,
          answer_numeric: answerNumeric,
          score: result.data.score
        }
      };
      setResponses(newResponses);
      saveResponsesLocally(newResponses);

      return result.data.score;
    } catch (err) {
      console.error('Error saving response:', err);
      throw err;
    }
  };

  // Navigate to next step
  const handleNext = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  // Navigate to previous step
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Calculate results
  const handleCalculate = async (valuationData, wealthData) => {

    try {
      setIsCalculating(true);

      // Move to Calculating page immediately
      handleNext();

      const result = await calculateLeadAssessmentResults(
        sessionToken,
        valuationData,
        wealthData
      );


      if (!result.success) {
        console.error('API returned error:', result.error);
        throw new Error(result.error);
      }

      setCalculatedResults(result.data);
      setIsCalculating(false);
      // CalculatingStep will auto-advance to Lead Capture when isCalculating becomes false
    } catch (err) {
      console.error('Error calculating results:', err);
      console.error('Error details:', err.message, err.stack);
      setError(err.message || 'Failed to calculate results');
      setIsCalculating(false);
    }
  };

  // Submit lead information
  const handleSubmitLead = async (leadData) => {
    try {
      setIsSubmittingLead(true);

      const result = await submitLeadInformation(sessionToken, leadData);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Clear all local storage
      clearLocalResponses();
      clearLocalValuation();
      clearLocalWealthData();

      // Navigate to results page
      navigate(`/exit-readiness-results/${sessionToken}`);
    } catch (err) {
      console.error('Error submitting lead:', err);
      setError(err.message || 'Failed to submit information');
      setIsSubmittingLead(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="exit-readiness-loading">
        <div className="loading-spinner" />
        <p>Loading assessment...</p>
      </div>
    );
  }

  // Render error state
  if (error && !questions) {
    return (
      <div className="exit-readiness-error">
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button onClick={initializeAssessment}>Try Again</button>
      </div>
    );
  }

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onNext={handleNext} />;

      case 1:
        return (
          <BusinessExplanationStep
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );

      case 2:
        return (
          <SequentialQuestionStep
            stepNumber={3}
            totalSteps={9}
            title="Business Assessment Questions"
            description="Rate your business readiness compared to industry standards"
            questions={questions.business}
            responses={responses}
            onSaveResponse={handleSaveResponse}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );

      case 3:
        return (
          <PersonalExplanationStep
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );

      case 4:
        return (
          <SequentialQuestionStep
            stepNumber={5}
            totalSteps={9}
            title="Personal Readiness Questions"
            description="Assess your personal preparation for exit"
            questions={questions.personal}
            responses={responses}
            onSaveResponse={handleSaveResponse}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );

      case 5:
        return (
          <ValuationInputStep
            fields={questions.valuation.fields}
            onNext={handleNext}
            onPrev={handlePrev}
            onSaveValuation={(data) => {
              // Store valuation data temporarily
              setCalculatedResults(prev => ({ ...prev, valuationData: data }));
              handleNext();
            }}
          />
        );

      case 6:
        return (
          <WealthGapInputStep
            fields={questions.wealth.fields}
            onNext={handleNext}
            onPrev={handlePrev}
            onCalculate={(wealthData) => {
              // Always fetch latest valuation data from localStorage
              const savedValuation = getLocalValuation();

              const valuationToUse = Object.keys(savedValuation).length > 0
                ? savedValuation
                : calculatedResults?.valuationData || {};

              // Convert valuation values to numbers
              const valuationConverted = {};
              Object.entries(valuationToUse).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                  // If it's industry (string) keep as string, otherwise convert to number
                  if (key === 'industry') {
                    valuationConverted[key] = value;
                  } else {
                    valuationConverted[key] = parseFloat(value.toString().replace(/,/g, ''));
                  }
                }
              });


              handleCalculate(valuationConverted, wealthData);
            }}
          />
        );

      case 7:
        return (
          <CalculatingStep
            isCalculating={isCalculating}
            onComplete={handleNext}
          />
        );

      case 8:
        return (
          <LeadCaptureStep
            previewScore={calculatedResults?.scores?.overall_exit_readiness_score}
            onSubmit={handleSubmitLead}
            isSubmitting={isSubmittingLead}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="exit-readiness-assessment"
      style={{
        backgroundImage: backgroundLoaded ? `url(${process.env.PUBLIC_URL}/Background.png)` : 'none',
        backgroundColor: backgroundLoaded ? 'transparent' : '#1a3a2e',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        transition: 'background-image 0.3s ease-in'
      }}
    >
      <div className="assessment-container">
        {renderStepContent()}
      </div>
    </div>
  );
};

// Step 0: Welcome
const WelcomeStep = ({ onNext }) => {
  return (
    <div className="welcome-step-wrapper">
      <div className="welcome-logo-container">
        <img
          src={`${process.env.PUBLIC_URL}/Login Logo.png`}
          alt="Dominion Business Advisors"
          className="welcome-logo"
        />
      </div>
      <div className="welcome-step">
        <div className="welcome-content">
          <h1 className="welcome-title">Exit Readiness Assessment</h1>
          <p className="welcome-subtitle">
            Discover your business's readiness for a successful exit in just 10-12 minutes
          </p>

        <div className="welcome-benefits">
          <h3>What You'll Receive:</h3>
          <ul>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Overall Exit Readiness Score (0-100%)</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Business & Personal Readiness Analysis</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Business Valuation Estimate</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Wealth Gap Analysis</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Personalized Recommendations</span>
            </li>
            <li>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>Downloadable PDF Report</span>
            </li>
          </ul>
        </div>

        <div className="welcome-time-estimate">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>Estimated time: 10-12 minutes</span>
        </div>

        <button className="welcome-start-btn" onClick={onNext}>
          Start Assessment
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>

        <p className="welcome-privacy">
          Your responses are private and secure. No login required.
        </p>

        {/* Copyright Footer */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
          <div className="assessment-footer">
            © 2025 Dominion Business Advisors LLC. All Rights Reserved.
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

// Step 4: Valuation Input
const ValuationInputStep = ({ fields, onNext, onPrev, onSaveValuation }) => {
  const [values, setValues] = useState({});
  const [displayValues, setDisplayValues] = useState({});

  // Load saved valuation data on mount
  useEffect(() => {
    const savedValuation = getLocalValuation();
    if (Object.keys(savedValuation).length > 0) {
      setValues(savedValuation);

      // Format currency fields for display
      const formatted = {};
      Object.entries(savedValuation).forEach(([key, value]) => {
        const field = fields.find(f => f.field_id === key);
        if (field && field.input_type === 'currency') {
          formatted[key] = formatCurrency(value);
        }
      });
      setDisplayValues(formatted);
    }
  }, [fields]);

  const formatCurrency = (value) => {
    if (!value) return '';
    // Remove non-digits
    const numericValue = value.toString().replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    // Format with commas
    return new Intl.NumberFormat('en-US').format(parseInt(numericValue, 10));
  };

  const handleCurrencyChange = (fieldId, displayValue) => {
    const numericValue = displayValue.replace(/[^0-9]/g, '');
    const newValues = { ...values, [fieldId]: numericValue };
    setValues(newValues);
    setDisplayValues(prev => ({ ...prev, [fieldId]: formatCurrency(numericValue) }));
    saveValuationLocally(newValues);
  };

  const handleChange = (fieldId, value) => {
    const newValues = { ...values, [fieldId]: value };
    setValues(newValues);
    saveValuationLocally(newValues);
  };

  const handleNextClick = () => {
    saveValuationLocally(values);
    onSaveValuation(values);
  };

  // Validation: All required fields must be filled (including industry)
  const requiredFieldsFilled = fields
    .filter(f => f.required)
    .every(f => values[f.field_id] && values[f.field_id].toString().trim() !== '');

  const canProceed = requiredFieldsFilled;

  return (
    <WizardStep
      stepNumber={6}
      totalSteps={9}
      title="Business Valuation"
      description="Provide valuation context (informational only)"
      onNext={handleNextClick}
      onPrev={onPrev}
      nextDisabled={!canProceed}
    >
      <div className="valuation-inputs">
        {fields.map(field => (
          <div key={field.field_id} className="input-field">
            <label className="input-label">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>

            {field.field_id === 'industry' ? (
              <>
                <IndustrySelector
                  value={values[field.field_id] || ''}
                  onChange={(value) => handleChange(field.field_id, value)}
                  required={field.required}
                />
                <p className="input-help" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  Select from the dropdown or type your custom industry name. This field is required - if your industry isn't listed, simply type it in (e.g., "Specialty Manufacturing", "Healthcare Services", etc.).
                </p>
              </>
            ) : field.input_type === 'currency' ? (
              <div className="currency-input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  value={displayValues[field.field_id] || ''}
                  onChange={(e) => handleCurrencyChange(field.field_id, e.target.value)}
                  className="input-control currency-input"
                  placeholder="0"
                  required={field.required}
                />
              </div>
            ) : field.input_type === 'select' ? (
              <select
                value={values[field.field_id] || ''}
                onChange={(e) => handleChange(field.field_id, e.target.value)}
                className="input-control"
                required={field.required}
              >
                <option value="">Select...</option>
                {field.options.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                type={field.input_type}
                value={values[field.field_id] || ''}
                onChange={(e) => handleChange(field.field_id, e.target.value)}
                className="input-control"
                placeholder={field.placeholder || field.help_text || ''}
                required={field.required}
                step={field.field_id === 'valuation_multiple' ? '0.1' : undefined}
                min={field.field_id === 'valuation_multiple' ? '0' : undefined}
              />
            )}

            {field.field_id === 'valuation_multiple' ? (
              <p className="input-help" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                Optional: Enter a custom EBITDA multiple (e.g., 3.5x) if you know your specific valuation multiple. If left blank, we'll use industry-standard multiples based on your industry selection.
              </p>
            ) : field.help_text && field.field_id !== 'industry' ? (
              <p className="input-help">{field.help_text}</p>
            ) : null}
          </div>
        ))}
      </div>
    </WizardStep>
  );
};

// Step 5: Wealth Gap Input
const WealthGapInputStep = ({ fields, onPrev, onCalculate }) => {
  const [calculationMethod, setCalculationMethod] = useState('direct'); // 'direct' or 'expenses'
  const [values, setValues] = useState({});
  const [displayValues, setDisplayValues] = useState({});
  const [annualExpenses, setAnnualExpenses] = useState('');
  const [displayExpenses, setDisplayExpenses] = useState('');

  // Load saved wealth data on mount
  useEffect(() => {
    const savedWealth = getLocalWealthData();
    if (Object.keys(savedWealth).length > 0) {
      // Check if using expenses method
      if (savedWealth.annual_expenses) {
        setCalculationMethod('expenses');
        setAnnualExpenses(savedWealth.annual_expenses);
        setDisplayExpenses(formatCurrency(savedWealth.annual_expenses));
      }

      setValues(savedWealth);

      // Format currency fields for display
      const formatted = {};
      Object.entries(savedWealth).forEach(([key, value]) => {
        if (key !== 'annual_expenses' && value) {
          formatted[key] = formatCurrency(value);
        }
      });
      setDisplayValues(formatted);
    }
  }, [fields]);

  // Calculate wealth goal from expenses (4% rule)
  useEffect(() => {
    if (calculationMethod === 'expenses' && annualExpenses) {
      const wealthGoal = Math.round(parseFloat(annualExpenses) / 0.04);
      const newValues = { ...values, desired_exit_proceeds: wealthGoal.toString(), annual_expenses: annualExpenses };
      setValues(newValues);
      setDisplayValues(prev => ({ ...prev, desired_exit_proceeds: formatCurrency(wealthGoal) }));
      saveWealthDataLocally(newValues);
    }
  }, [annualExpenses, calculationMethod]);

  const formatCurrency = (value) => {
    if (!value) return '';
    const numericValue = value.toString().replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    return new Intl.NumberFormat('en-US').format(parseInt(numericValue, 10));
  };

  const handleCurrencyChange = (fieldId, displayValue) => {
    const numericValue = displayValue.replace(/[^0-9]/g, '');
    const newValues = { ...values, [fieldId]: numericValue };
    // Remove annual_expenses if switching to direct method
    if (fieldId === 'desired_exit_proceeds' && calculationMethod === 'expenses') {
      delete newValues.annual_expenses;
    }
    setValues(newValues);
    setDisplayValues(prev => ({ ...prev, [fieldId]: formatCurrency(numericValue) }));
    saveWealthDataLocally(newValues);
  };

  const handleExpensesChange = (displayValue) => {
    const numericValue = displayValue.replace(/[^0-9]/g, '');
    setAnnualExpenses(numericValue);
    setDisplayExpenses(formatCurrency(numericValue));
  };

  const handleMethodChange = (method) => {
    setCalculationMethod(method);
    if (method === 'direct') {
      // Clear expenses and recalculate
      setAnnualExpenses('');
      setDisplayExpenses('');
      const newValues = { ...values };
      delete newValues.annual_expenses;
      setValues(newValues);
      saveWealthDataLocally(newValues);
    }
  };

  const handleCalculateClick = () => {

    const finalValues = {};
    // Convert all string values to numbers and remove annual_expenses
    Object.entries(values).forEach(([key, value]) => {
      if (key !== 'annual_expenses' && value !== null && value !== undefined && value !== '') {
        // Convert to number (remove any commas and parse as float)
        finalValues[key] = parseFloat(value.toString().replace(/,/g, ''));
      }
    });


    saveWealthDataLocally(finalValues);

    onCalculate(finalValues);
  };

  const isReadyToCalculate = () => {
    const hasWealthGoal = calculationMethod === 'direct'
      ? !!(values.desired_exit_proceeds && values.desired_exit_proceeds !== '')
      : !!(annualExpenses && annualExpenses !== '');

    const hasNetWorth = !!(values.current_net_worth && values.current_net_worth !== '');

    const ready = hasWealthGoal && hasNetWorth;

    return ready;
  };

  return (
    <WizardStep
      stepNumber={7}
      totalSteps={9}
      title="Wealth Gap Analysis"
      description="Identify any gap between business value and financial goals"
      onNext={handleCalculateClick}
      onPrev={onPrev}
      nextLabel="Calculate Results"
      nextDisabled={!isReadyToCalculate()}
    >
      <div className="wealth-gap-inputs">
        {/* Wealth Goal Selection Method */}
        <div className="input-field">
          <label className="input-label">
            How would you like to set your wealth goal?
            <span className="required">*</span>
          </label>
          <div className="calculation-method-selector">
            <button
              type="button"
              className={`method-button ${calculationMethod === 'direct' ? 'active' : ''}`}
              onClick={() => handleMethodChange('direct')}
            >
              <div className="method-title">Direct Amount</div>
              <div className="method-description">Enter your target exit proceeds</div>
            </button>
            <button
              type="button"
              className={`method-button ${calculationMethod === 'expenses' ? 'active' : ''}`}
              onClick={() => handleMethodChange('expenses')}
            >
              <div className="method-title">From Expenses (4% Rule)</div>
              <div className="method-description">Calculate from annual expenses</div>
            </button>
          </div>
        </div>

        {/* Direct Method */}
        {calculationMethod === 'direct' && (
          <div className="input-field">
            <label className="input-label">
              Target Exit Proceeds
              <span className="required">*</span>
            </label>
            <div className="currency-input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                type="text"
                value={displayValues.desired_exit_proceeds || ''}
                onChange={(e) => handleCurrencyChange('desired_exit_proceeds', e.target.value)}
                className="input-control currency-input"
                placeholder="0"
                required
              />
            </div>
            <p className="input-help">
              The amount of after-tax proceeds you need from your business exit to achieve your financial goals
            </p>
          </div>
        )}

        {/* Expenses Method */}
        {calculationMethod === 'expenses' && (
          <>
            <div className="input-field">
              <label className="input-label">
                Annual Living Expenses
                <span className="required">*</span>
              </label>
              <div className="currency-input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  value={displayExpenses}
                  onChange={(e) => handleExpensesChange(e.target.value)}
                  className="input-control currency-input"
                  placeholder="0"
                  required
                />
              </div>
              <p className="input-help">
                Your desired annual spending after exiting the business
              </p>
            </div>

            {annualExpenses && (
              <div className="calculation-result">
                <div className="result-label">Calculated Wealth Goal (4% Rule):</div>
                <div className="result-value">${formatCurrency(values.desired_exit_proceeds || 0)}</div>
                <p className="result-help">
                  Using the 4% rule, you would need ${formatCurrency(values.desired_exit_proceeds || 0)} to safely withdraw ${formatCurrency(annualExpenses)} per year
                </p>
              </div>
            )}

            <div className="wealth-disclosure">
              <strong>About the 4% Rule:</strong>
              <p>
                The 4% rule suggests that withdrawing 4% of your portfolio annually (adjusted for inflation) provides a high probability of lasting 30+ years. This is a general guideline and not financial advice. Your specific needs may vary based on lifestyle, market conditions, and longevity.
              </p>
            </div>
          </>
        )}

        {/* Personal Net Worth */}
        <div className="input-field">
          <label className="input-label">
            Current Personal Net Worth (Outside of Business)
            <span className="required">*</span>
          </label>
          <div className="currency-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="text"
              value={displayValues.current_net_worth || ''}
              onChange={(e) => handleCurrencyChange('current_net_worth', e.target.value)}
              className="input-control currency-input"
              placeholder="0"
              required
            />
          </div>
          <div className="input-help-extended">
            <p><strong>What to include:</strong></p>
            <ul>
              <li>Retirement accounts (401k, IRA, etc.)</li>
              <li>Investment accounts and cash savings</li>
              <li>Real estate equity (excluding primary residence if needed for retirement)</li>
              <li>Other liquid investments</li>
            </ul>
            <p><strong>What to exclude:</strong> Your business value (we'll calculate that separately)</p>
            <p><strong>Why this matters:</strong> Knowing your net worth outside the business helps us identify the true gap between your current position and your wealth goal.</p>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};

// Step 6: Calculating
const CalculatingStep = ({ isCalculating, onComplete }) => {
  // Auto-advance when calculation completes
  useEffect(() => {
    if (!isCalculating && onComplete) {
      // Small delay to show success animation
      setTimeout(() => {
        onComplete();
      }, 500);
    }
  }, [isCalculating, onComplete]);

  return (
    <div className="calculating-step">
      <div className="calculating-content">
        <div className="calculating-spinner" />
        <h2>Calculating Your Results...</h2>
        <p>Analyzing your responses and generating personalized recommendations</p>
        <div className="calculating-progress">
          <div className="progress-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Business readiness analysis</span>
          </div>
          <div className="progress-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Personal readiness analysis</span>
          </div>
          <div className="progress-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Valuation estimation</span>
          </div>
          <div className="progress-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Generating recommendations</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step 7: Lead Capture
const LeadCaptureStep = ({ previewScore, onSubmit, isSubmitting }) => {
  return (
    <div className="lead-capture-step">
      <LeadCaptureForm
        previewScore={previewScore}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default ExitReadinessAssessment;
